import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PageHeader } from "@/components/app/page-header";
import { HelpPanel } from "@/components/app/help-panel";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  getBounceInboxSettings,
  updateBounceInboxSettings,
  listBounceScanRuns,
} from "@/lib/bounce-inbox-settings.functions";
import {
  testBounceImap,
  triggerBounceScanNow,
  simulateTestBounce,
} from "@/lib/bounce-inbox-test.functions";

export const Route = createFileRoute("/_authenticated/bounce-inbox-settings")({
  component: BounceInboxSettingsPage,
  head: () => ({ meta: [{ title: "Bounce-Inbox — InboxPulse" }] }),
});

function BounceInboxSettingsPage() {
  const fetchSettings = useServerFn(getBounceInboxSettings);
  const saveSettings = useServerFn(updateBounceInboxSettings);
  const fetchRuns = useServerFn(listBounceScanRuns);
  const runTestImap = useServerFn(testBounceImap);
  const runScanNow = useServerFn(triggerBounceScanNow);
  const runSimulate = useServerFn(simulateTestBounce);
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["bounce-inbox-settings"],
    queryFn: () => fetchSettings(),
  });
  const { data: runsData } = useQuery({
    queryKey: ["bounce-scan-runs"],
    queryFn: () => fetchRuns(),
    refetchInterval: 15000,
  });

  const [form, setForm] = useState({
    enabled: false,
    bounce_domain: "",
    imap_host: "",
    imap_port: 993,
    imap_tls: true,
    imap_username: "",
    password: "",
    scanner_url: "",
    scanner_secret: "",
    interval_minutes: 2,
    lookback_minutes: 60,
    regenerate_verp_secret: false,
  });

  useEffect(() => {
    const s = data?.settings;
    if (s) {
      setForm((p) => ({
        ...p,
        enabled: s.enabled,
        bounce_domain: s.bounce_domain ?? "",
        imap_host: s.imap_host ?? "",
        imap_port: s.imap_port,
        imap_tls: s.imap_tls,
        imap_username: s.imap_username ?? "",
        scanner_url: s.scanner_url ?? "",
        interval_minutes: s.interval_minutes,
        lookback_minutes: s.lookback_minutes,
        password: "",
        scanner_secret: "",
        regenerate_verp_secret: false,
      }));
    }
  }, [data]);

  const mutation = useMutation({
    mutationFn: () => {
      const payload: Record<string, unknown> = {
        enabled: form.enabled,
        imap_port: form.imap_port,
        imap_tls: form.imap_tls,
        interval_minutes: form.interval_minutes,
        lookback_minutes: form.lookback_minutes,
      };
      if (form.bounce_domain) payload.bounce_domain = form.bounce_domain;
      if (form.imap_host) payload.imap_host = form.imap_host;
      if (form.imap_username) payload.imap_username = form.imap_username;
      if (form.password) payload.password = form.password;
      if (form.scanner_url) payload.scanner_url = form.scanner_url;
      if (form.scanner_secret) payload.scanner_secret = form.scanner_secret;
      if (form.regenerate_verp_secret) payload.regenerate_verp_secret = true;
      return saveSettings({ data: payload as never });
    },
    onSuccess: () => {
      toast.success("Gespeichert");
      qc.invalidateQueries({ queryKey: ["bounce-inbox-settings"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const testImapMut = useMutation({
    mutationFn: () => runTestImap(),
    onSuccess: (r) => {
      if (r.ok) {
        toast.success(
          `IMAP ok · ${r.inbox?.messages ?? 0} msgs (${r.inbox?.unseen ?? 0} ungelesen) · ${r.duration_ms}ms`,
        );
      } else {
        toast.error(`IMAP-Fehler: ${r.error ?? "unknown"}`);
      }
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const scanNowMut = useMutation({
    mutationFn: () => runScanNow(),
    onSuccess: (r) => {
      if (r.ok) toast.success(`Scan ausgelöst: ${JSON.stringify(r.details)}`);
      else toast.error(`Scan-Fehler (HTTP ${r.status}): ${JSON.stringify(r.details)}`);
      qc.invalidateQueries({ queryKey: ["bounce-scan-runs"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
  const simulateMut = useMutation({
    mutationFn: () => runSimulate(),
    onSuccess: (r) =>
      toast.success(`Test-Bounce eingefügt (${r.recipient}) · Telegram: ${r.telegram}`),
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading) return <div className="p-8 text-muted-foreground">Lade…</div>;
  if (error) return <div className="p-8 text-destructive">{(error as Error).message}</div>;

  return (
    <div className="p-8 max-w-3xl space-y-6">
      <PageHeader
        title="Bounce-Inbox (VERP)"
        description="Zentrale Bounce-Domain mit IMAP-Catch-All. Verarbeitet DSN-Reports und löst Telegram-Alerts aus."
      />

      <HelpPanel
        title="Hilfe: Wohin sollen Bounces zurückgeschickt werden?"
        what={
          <>
            Hier legst du <b>eine Sammel-Adresse</b> fest, an die alle Bounce-Meldungen gehen. Das
            System liest dieses Postfach automatisch aus und wertet jede Rück-Mail aus.
          </>
        }
        why={
          <>
            Ohne zentrale Bounce-Adresse gehen Fehlermeldungen ins Leere — du erfährst nie, welche
            Adressen tot sind.
          </>
        }
        how={[
          <>
            <b>Bounce-Domain</b> einrichten (z.B. <code>bounces.deinefirma.de</code>) — DNS-Eintrag
            muss zeigen auf unseren Server.
          </>,
          <>
            <b>IMAP-Zugang</b> hinterlegen (Server, Benutzer, Passwort).
          </>,
          <>
            <b>Verbindung testen</b> — grünes Häkchen = wir lesen das Postfach.
          </>,
          <>
            Bounces erscheinen ab jetzt automatisch unter <b>Bounce-Log</b>.
          </>,
        ]}
        tips={[
          <>
            Catch-All bedeutet: alle Mails an *@bounces.deinefirma.de landen in einem Postfach.
            Wichtig für VERP.
          </>,
        ]}
        glossary={[
          {
            term: "Catch-All",
            def: "Postfach, das ALLE Mails einer Domain auffängt, egal welche Adresse vor dem @ steht.",
          },
          { term: "IMAP", def: "Protokoll, um Mails von einem Server zu lesen." },
          {
            term: "DSN",
            def: "Delivery Status Notification — die offizielle Fehler-Mail vom Server des Empfängers.",
          },
        ]}
      />

      <Card className="p-6 bg-card/60 border-border space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <Label>Scan aktiv</Label>
            <p className="text-xs text-muted-foreground">Cron darf das Postfach abrufen.</p>
          </div>
          <Switch
            checked={form.enabled}
            onCheckedChange={(v) => setForm((p) => ({ ...p, enabled: v }))}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2 col-span-2">
            <Label>Bounce-Domain</Label>
            <Input
              placeholder="bounce-monitor.de"
              value={form.bounce_domain}
              onChange={(e) => setForm((p) => ({ ...p, bounce_domain: e.target.value }))}
            />
            <p className="text-xs text-muted-foreground">
              VERP-Adressen entstehen als <code>b+camp+rid+sig@bounce-domain</code>.
            </p>
          </div>
          <div className="space-y-2">
            <Label>IMAP Host</Label>
            <Input
              placeholder="imap.example.com"
              value={form.imap_host}
              onChange={(e) => setForm((p) => ({ ...p, imap_host: e.target.value }))}
            />
          </div>
          <div className="space-y-2">
            <Label>IMAP Port</Label>
            <Input
              type="number"
              value={form.imap_port}
              onChange={(e) =>
                setForm((p) => ({ ...p, imap_port: Number.parseInt(e.target.value, 10) || 993 }))
              }
            />
          </div>
          <div className="space-y-2">
            <Label>Benutzername (Catch-All)</Label>
            <Input
              placeholder="catchall@bounce-monitor.de"
              value={form.imap_username}
              onChange={(e) => setForm((p) => ({ ...p, imap_username: e.target.value }))}
            />
          </div>
          <div className="space-y-2">
            <Label>
              Passwort{" "}
              {data?.passwordConfigured ? <Badge variant="secondary">gesetzt</Badge> : null}
            </Label>
            <Input
              type="password"
              placeholder={data?.passwordConfigured ? "•••• (leer = unverändert)" : ""}
              value={form.password}
              onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
            />
          </div>
          <div className="flex items-center gap-2 col-span-2">
            <Switch
              checked={form.imap_tls}
              onCheckedChange={(v) => setForm((p) => ({ ...p, imap_tls: v }))}
            />
            <Label>TLS</Label>
          </div>
        </div>
      </Card>

      <Card className="p-6 bg-card/60 border-border space-y-5">
        <h3 className="font-semibold">Externer Scanner</h3>
        <div className="space-y-2">
          <Label>Scanner-URL</Label>
          <Input
            placeholder="https://scanner.example.com"
            value={form.scanner_url}
            onChange={(e) => setForm((p) => ({ ...p, scanner_url: e.target.value }))}
          />
          <p className="text-xs text-muted-foreground">
            Erwartet <code>POST /scan-bounces</code>.
          </p>
        </div>
        <div className="space-y-2">
          <Label>
            Scanner-Secret{" "}
            {data?.scannerSecretConfigured ? <Badge variant="secondary">gesetzt</Badge> : null}
          </Label>
          <Input
            type="password"
            placeholder={data?.scannerSecretConfigured ? "•••• (leer = unverändert)" : ""}
            value={form.scanner_secret}
            onChange={(e) => setForm((p) => ({ ...p, scanner_secret: e.target.value }))}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Intervall (Min.)</Label>
            <Input
              type="number"
              value={form.interval_minutes}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  interval_minutes: Number.parseInt(e.target.value, 10) || 2,
                }))
              }
            />
          </div>
          <div className="space-y-2">
            <Label>Lookback (Min.)</Label>
            <Input
              type="number"
              value={form.lookback_minutes}
              onChange={(e) =>
                setForm((p) => ({
                  ...p,
                  lookback_minutes: Number.parseInt(e.target.value, 10) || 60,
                }))
              }
            />
          </div>
        </div>
      </Card>

      <Card className="p-6 bg-card/60 border-border space-y-3">
        <h3 className="font-semibold">VERP-Secret</h3>
        <p className="text-xs text-muted-foreground">
          Wird in HMAC-Signaturen jeder Return-Path-Adresse verwendet.{" "}
          {data?.verpSecretConfigured ? (
            <Badge variant="secondary">gesetzt</Badge>
          ) : (
            <Badge variant="destructive">fehlt</Badge>
          )}
        </p>
        <div className="flex items-center gap-2">
          <Switch
            checked={form.regenerate_verp_secret}
            onCheckedChange={(v) => setForm((p) => ({ ...p, regenerate_verp_secret: v }))}
          />
          <Label>Beim Speichern neu generieren (invalidiert alte VERP-Adressen)</Label>
        </div>
        {!data?.encryptionKeyConfigured && (
          <p className="text-xs text-destructive">
            IMAP_ENCRYPTION_KEY ist nicht gesetzt. Passwort-Speicherung deaktiviert.
          </p>
        )}
      </Card>

      <div className="flex justify-between items-center">
        <p className="text-xs text-muted-foreground">
          Status: {data?.settings?.last_status ?? "–"}
          {data?.settings?.last_triggered_at
            ? ` · ${new Date(data.settings.last_triggered_at).toLocaleString()}`
            : ""}
        </p>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => testImapMut.mutate()}
            disabled={testImapMut.isPending}
          >
            {testImapMut.isPending ? "Teste…" : "IMAP testen"}
          </Button>
          <Button
            variant="outline"
            onClick={() => scanNowMut.mutate()}
            disabled={scanNowMut.isPending}
          >
            {scanNowMut.isPending ? "Scanne…" : "Jetzt scannen"}
          </Button>
          <Button
            variant="outline"
            onClick={() => simulateMut.mutate()}
            disabled={simulateMut.isPending}
          >
            {simulateMut.isPending ? "Sende…" : "Test-Bounce"}
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
            className="bg-[image:var(--gradient-primary)] hover:opacity-90"
          >
            {mutation.isPending ? "Speichere…" : "Speichern"}
          </Button>
        </div>
      </div>

      <Card className="p-6 bg-card/60 border-border space-y-2">
        <h3 className="font-semibold">Letzte Scan-Läufe</h3>
        <div className="space-y-1 text-sm">
          {(runsData?.runs ?? []).slice(0, 10).map((r) => (
            <div
              key={r.id}
              className="flex items-center justify-between border-b border-border py-1"
            >
              <span>{new Date(r.started_at).toLocaleString()}</span>
              <span className="flex gap-3 text-xs text-muted-foreground">
                <span>{r.messages_fetched} msgs</span>
                <span>{r.bounces_parsed} parsed</span>
                <span>{r.errors} err</span>
                <Badge variant={r.ok ? "secondary" : "destructive"}>{r.ok ? "ok" : "fail"}</Badge>
              </span>
            </div>
          ))}
          {(runsData?.runs ?? []).length === 0 && (
            <p className="text-muted-foreground text-xs">Noch keine Läufe.</p>
          )}
        </div>
      </Card>
    </div>
  );
}
